// Backend client. Endpoints live in netlify/functions and are routed at /api/*.

export type RedeemResult =
  | { paired: true; email: string; initials?: string }
  | { paired: false; error: string };

// Redeem the QR-2 pairing token (scanned in-app) OR the manual short-code. Both
// resolve to the same Neon pairing record minted by the pane's /api/scan/pair.
export async function redeemPairing(input: { token?: string; code?: string }): Promise<RedeemResult> {
  const r = await fetch("/api/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  return (await r.json()) as RedeemResult;
}

export type SignStatus = { synced: boolean; name: string };

// Does this paired user have a signature synced from their desktop Doc Signer? Drives the
// "Sign" pill on the capture screen. Status only — the signature bytes never come to the phone.
export async function getSignStatus(): Promise<SignStatus> {
  try {
    const r = await fetch("/api/signing/status", { credentials: "include" });
    if (!r.ok) return { synced: false, name: "" };
    return (await r.json()) as SignStatus;
  } catch {
    return { synced: false, name: "" };
  }
}

// NOTE: org membership (request/accept/decline) is NOT callable from the companion (Gery 2026-06-23) —
// enrolling / picking a plan happens only in the Outlook pane / desktop PWA, so no client helper here.

export type ProcessResult = { ok: boolean; error?: string };

// Upload the captured pages (already downscaled). Processing is async — the result
// arrives by email; this call just enqueues and returns quickly. `sign` → the server stamps
// the assembled PDF with the user's synced signature before emailing it.
export async function processScan(pages: Blob[], sign = false, diag: string[] = [], corners: (number[][] | null)[] = []): Promise<ProcessResult> {
  const fd = new FormData();
  pages.forEach((p, i) => fd.append("pages", p, `page-${i + 1}.jpg`));
  if (sign) fd.append("sign", "1");
  // Capture-time detect diagnostics; the pipeline embeds them invisibly in the PDF's Keywords
  // metadata so a bad crop in a live test explains itself (see detect.ts lastDetectDiag).
  if (diag.length) fd.append("diag", diag.join(" | ").slice(0, 1000));
  // Per-page corner hints ([[x,y]×4] fractions or null) — the server crops; the phone never cuts.
  if (corners.length) fd.append("corners", JSON.stringify(corners));
  try {
    const r = await fetch("/api/docscan/process", { method: "POST", credentials: "include", body: fd });
    // Background function → 202 Accepted (no body); the finished PDF arrives by email.
    return { ok: r.ok || r.status === 202 };
  } catch (e) {
    // Network / upload failure — surface the error state instead of a silent unhandled rejection.
    return { ok: false, error: String((e as Error)?.message ?? e) };
  }
}
